import { LoadingButton } from "@mui/lab";
import { Box, Button, Stack } from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  Controls,
  EditMode,
  Plan,
  PlanType,
  RenewalOptions,
  RenewalStrategy,
  RenewalType,
  ServiceSector,
  Tier
} from "../../models";
import { EmployerModuleType } from "../../models/accounts/employerModuleType";
import { PlanFormValues } from "./FormTypes/PlanFormValues";
import { EmployerModuleOptions } from "../../models/accounts/employerModuleOptions";

type PlanFormProps = {
  serviceSectors: ServiceSector[];
  existingPlan?: Plan | null;
  moduleType: EmployerModuleType;
  customDefaultValues?: Partial<PlanFormValues>;
  isLoading: boolean;
  onClose: () => void;
  onSave: (values: PlanFormOutput) => void;
  children: React.ReactNode;
};

const PlanModuleTypeToName = {
  [EmployerModuleType.GIFTING]: "Gift",
  [EmployerModuleType.ALLOWANCES]: "Allowance",
  [EmployerModuleType.PUBLIC_TRANSPORT]: "", // No module name on public transport form button
  [EmployerModuleType.REWARDS_RECOGNITION]: ""
};

export interface PlanFormOutput {
  name: string;
  type: PlanType;
  expiryPeriod: number;
  tiers: Tier[];
  restrictionOption: string;
  serviceSectorIds: string[];
  controls?: Controls
  renewalOptions: RenewalOptions

}

const generateDefaultValues = (
  editMode: EditMode,
  existingPlan?: Plan | null,
  customDefaultValues?: Partial<PlanFormValues>,
  openServiceSector?: ServiceSector | null
) => {
  if (editMode === EditMode.CREATE) {
    return {
      name: "",
      type: PlanType.ONE_OFF,
      renewalType: RenewalType.INDIVIDUAL_START_DATE,
      strategy: RenewalStrategy.RESET,
      expiryPeriod: 12,
      tiers: [],
      restrictionOption: "open",
      serviceSectorIds: [],
      controls: {} as Controls,
      renewalOptions: { 
        type: RenewalType.INDIVIDUAL_START_DATE,
        strategy: RenewalStrategy.RESET,
        periodInMonths: 12 
      },
      startDate: "",
      employerModuleOptions: {} as EmployerModuleOptions,
      ...customDefaultValues
    };
  }
  const initialServiceSectorIds =
    existingPlan?.controls?.serviceSectors?.map((x) => x.id) ?? [];
  const isOpenSelected =
    openServiceSector &&
    initialServiceSectorIds.length === 1 &&
    initialServiceSectorIds[0] === openServiceSector.id;

  return {
    name: existingPlan?.name ?? "",
    type: existingPlan?.type ?? PlanType.ONE_OFF,
    expiryPeriod: existingPlan?.renewalOptions?.periodInMonths ?? 12,
    strategy: existingPlan?.renewalOptions?.strategy ?? RenewalStrategy.RESET,
    tiers: existingPlan?.tiers ?? [],
    restrictionOption: isOpenSelected ? "open" : "restricted",
    serviceSectorIds: initialServiceSectorIds,
    renewalType: existingPlan?.renewalOptions?.type ?? RenewalType.INDIVIDUAL_START_DATE,
    controls: existingPlan?.controls ?? {} as Controls,
    renewalOptions: existingPlan?.renewalOptions ?? {} as RenewalOptions,
    startDate: existingPlan?.renewalOptions?.startDate ?? "",
    employerModuleOptions: existingPlan?.employerModuleOptions ?? {} as EmployerModuleOptions
  };
};

/**
 * PlanForm is a form for creating and editing a plan.
 * It creates a bootstrap form for the plan details.
 * It does not provide UI implementation for the form fields - you must provide your own as children of this component.
 * These will likely differ depending on the module type.
 * @param serviceSectors - the service sectors of the employer
 * @param existingPlan - the existing plan, if editing
 * @param isLoading - whether the form is loading
 * @param onClose - the function to call when the form is closed
 * @param onSave - the function to call when the form is saved
 * @returns
 */
export const PlanFormBase = ({
  serviceSectors,
  isLoading,
  onClose,
  onSave,
  existingPlan,
  moduleType,
  children,
  customDefaultValues
}: PlanFormProps) => {
  const editMode = existingPlan == null ? EditMode.CREATE : EditMode.EDIT;

  const getOpenServiceSector = () => {
    return serviceSectors.find(
      (ss) =>
        ss.status === "ACTIVE" &&
        ss.restrictions.mccCodes.allow.length === 0 &&
        ss.restrictions.mccCodes.block.length === 0 &&
        ss.restrictions.providerNames.allow.length === 0 &&
        ss.restrictions.providerNames.block.length === 0
    );
  };
  const openServiceSector = getOpenServiceSector();

  const initialValues = generateDefaultValues(
    editMode,
    existingPlan,
    customDefaultValues,
    openServiceSector
  );

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    expiryPeriod: Yup.number().required("Expiry period is required"),
    tiers: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required("Tier name is required"),
          amount: Yup.object().shape({
            amount: Yup.number()
              .required("Amount is required")
              .test(
                "is-valid-amount",
                moduleType === EmployerModuleType.PUBLIC_TRANSPORT
                  ? "Amount must be 0"
                  : "Amount must be greater than 0",
                (value) => {
                  if (moduleType === EmployerModuleType.PUBLIC_TRANSPORT) {
                    return value !== undefined && value === 0;
                  }
                  return value !== undefined && value > 0;
                }
              )
          })
        })
      )
      .min(1, "At least one tier is required"),
    serviceSectorIds: Yup.array().when("restrictionOption", {
      is: (val: string) => val === "restricted",
      then: () => Yup.array().min(1, "At least one category must be selected"),
      otherwise: () => Yup.array()
    })
  });

  const handleSubmit = async (values: PlanFormValues) => {
    const output: PlanFormOutput = {
      name: values.name,
      type: values.type,
      expiryPeriod: values.expiryPeriod,
      controls: values.controls,
      tiers: values.tiers,
      restrictionOption: values.restrictionOption,
      serviceSectorIds: values.restrictionOption === "open" && openServiceSector
        ? [openServiceSector.id]
        : values.serviceSectorIds,
      renewalOptions: {
        strategy: values.strategy,
        type: values.renewalType,
        periodInMonths: values.expiryPeriod === -1 ? null : values.expiryPeriod,
        startDate: values.startDate || null
      }
    };

    onSave(output);
  };

  return (
    <Box sx={{ width: "100%" }} padding={2}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {children}
              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={onClose}
                  sx={{ flex: 1 }}
                >
                  Cancel
                </Button>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isLoading || isSubmitting}
                  sx={{ flex: 1 }}
                >
                  {editMode === EditMode.CREATE
                    ? `Create ${PlanModuleTypeToName[moduleType]}`
                    : "Save Changes"}
                </LoadingButton>
              </Stack>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
